import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Divider,
    useTheme,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Breadcrumbs,
    Link,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, FieldArray, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../../helpers/AuthProvider";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import MainDrawer from "../../MainDrawer";

// --- Data for Dropdowns ---
const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg', disabled: true},
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg', disabled: true }
];
const gcpLocationList = [ "africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2", "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2", "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1", "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2", "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9", "me-central1", "me-central2", "me-west1", "northamerica-northeast1", "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1", "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2", "us-west3", "us-west4"];
const zoneTypes = ["RAW", "CURATED"];

// --- Interfaces ---
interface CostLabelPair { id?: string; cost: string; label: string; }
interface DataZoneFormValues {
    id?: string;
    name: string;
    cloudProvider: string;
    environment: string;
    parentSolution: string; // Lake ID
    location: string;
    type: string;
    description: string;
    costCenterValue: string;
    costLabelPairs: CostLabelPair[];
}
interface LakeData { id: string; name: string; }
interface AddDataZonesPageProps { isEdit: boolean; }
interface DataZonesFormContentProps { isEdit: boolean; }

// --- Main Form Content Component ---
const DataZonesFormContent: React.FC<DataZonesFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [availableLakes, setAvailableLakes] = useState<LakeData[]>([]);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [initialFormValuesForEdit, setInitialFormValuesForEdit] = useState<DataZoneFormValues | null>(null);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoadingData(true);
            try {
                const lakesRes = await queryGraphql(`query GetDataplexLakes { getDataplexLakes { id name } }`);
                if (lakesRes.getDataplexLakes) {
                    setAvailableLakes(lakesRes.getDataplexLakes);
                }

                if (isEdit && id) {
                    const response = await queryGraphql(`
                        query GetDataplexZone($getDataplexZoneId: String!) {
                          getDataplexZone(id: $getDataplexZoneId) {
                            id name cloudProvider parentSolution { id } costLabelPairs { cost label }
                            details { 
                              dataplexZone { 
                                location type description
                              } 
                            }
                          }
                        }
                    `, { getDataplexZoneId: id });

                    const dataZone = response.getDataplexZone;
                    if (dataZone) {
                        const allPairs = dataZone.costLabelPairs || [];
                        const costCenterPair = allPairs.find((p: any) => p.cost === "cost-center");
                        const dynamicPairs = allPairs.filter((p: any) => p.cost !== "cost-center").map((pair: any, index: number) => ({ ...pair, id: `clp-edit-${index}` }));
                        const dzDetails = dataZone.details.dataplexZone;
                        
                        setInitialFormValuesForEdit({
                            id: dataZone.id,
                            name: dataZone.name,
                            costCenterValue: costCenterPair ? costCenterPair.label : "",
                            costLabelPairs: dynamicPairs,
                            cloudProvider: dataZone.cloudProvider || 'gcp',
                            parentSolution: dataZone.parentSolution?.id || "",
                            environment: dataZone.environment || "dev",
                            location: dzDetails.location || "",
                            type: dzDetails.type || "RAW",
                            description: dzDetails.description || "",
                        });
                    } else {
                        navigate("/datasolutions/data-zones");
                    }
                }
            } catch (err) {
                console.error("Error fetching initial data:", err);
                if (isEdit) navigate("/datasolutions/data-zones");
            } finally {
                setIsLoadingData(false);
            }
        };
        fetchInitialData();
    }, [isEdit, id, navigate]);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("This field is required").max(50, "Max 50 characters"),
        cloudProvider: Yup.string().required("This field is required"),
        parentSolution: Yup.string().required("A parent lake must be selected"),
        environment: Yup.string().required("This field is required"),
        location: Yup.string().required("This field is required"),
        type: Yup.string().required("This field is required"),
        description: Yup.string().optional(),
        costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
        costLabelPairs: Yup.array().of(Yup.object().shape({
            cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
            label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
        })),
    });

    const defaultInitialValues: DataZoneFormValues = {
        name: "",
        cloudProvider: "gcp",
        parentSolution: "",
        environment: "dev",
        location: "asia-south1",
        type: "RAW",
        description: "",
        costCenterValue: "",
        costLabelPairs: [],
    };
    
    if (isLoadingData) {
        return <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading data...</Typography></Box>;
    }
    if (isEdit && !initialFormValuesForEdit) {
         return <Typography color="error" sx={{p:3}}>Could not load data zone for editing.</Typography>;
    }

    return (
        <Container sx={{ py: 3, px: { xs: 2, sm: 3, md: 4 }, flexGrow: 1 }}>
            <Formik
                initialValues={isEdit ? (initialFormValuesForEdit || defaultInitialValues) : defaultInitialValues}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting, setStatus }) => {
                    setIsSubmittingForm(true);
                    setSubmitting(true);

                    const finalCostLabelPairs = [{ cost: "cost-center", label: values.costCenterValue }, ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))];

                    const detailsPayload = {
                        location: values.location,
                        type: values.type,
                        description: values.description,
                    };

                    const commonInput = {
                        name: values.name,
                        cloudProvider: values.cloudProvider,
                        environment: values.environment,
                        parentSolution: values.parentSolution,
                        costLabelPairs: finalCostLabelPairs,
                        details: { dataplexZone: detailsPayload },
                    };

                    const mutationString = isEdit ? `mutation UpdateSolutionDataplexZone($input: UpdateSolutionInput) { updateSolutionDataplexZone(input: $input) { id } }` : `mutation CreateSolutionDataplexZone($input: CreateSolutionInput) { createSolutionDataplexZone(input: $input) { id } }`;
                    
                    const variables = { input: isEdit ? { id, ...commonInput } : commonInput };
                    const resultKey = isEdit ? 'updateSolutionDataplexZone' : 'createSolutionDataplexZone';

                    try {
                        const data = await queryGraphql(mutationString, variables);
                        if (data[resultKey] && data[resultKey].id) {
                            navigate("/datasolutions/data-zones");
                        } else {
                            setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} Data Zone. ${JSON.stringify(data.errors)}` });
                        }
                    } catch (error: any) {
                        setStatus({ error: error.message || "An unexpected error occurred." });
                    } finally {
                        setIsSubmittingForm(false);
                        setSubmitting(false);
                    }
                }}
            >
                {({ handleChange, handleBlur, values, setFieldValue, errors, touched, isValid, dirty, status, isSubmitting }) => (
                    <Form>
                        <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={2.5}>
                            {isEdit ? "Edit the Data Zone details below" : "Fill the below details to add a Data Zone"}
                        </Typography>
                        
                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Cloud Provider Details</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                    <ToggleButtonGroup
                                            value={values.cloudProvider}
                                            exclusive
                                            onChange={(event, newProvider) => {
                                                if (newProvider !== null) {
                                                    setFieldValue('cloudProvider', newProvider);
                                                }
                                            }}
                                            aria-label="cloud provider"
                                            sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                                        >
                                            {providerDisplayData.map((p) => (
                                                <ToggleButton
                                                    key={p.value}
                                                    value={p.value}
                                                    disabled={p.disabled}
                                                    aria-label={p.label}
                                                    sx={{ display: 'flex', gap: 1, alignItems: 'center', textTransform: 'none', fontFamily: 'Gilroy-Medium', borderRadius: '24px !important', border: '1px solid #ccc !important', px: 2, py: 1, '&.Mui-selected': { borderColor: '#000 !important', backgroundColor: 'rgba(0, 0, 0, 0.08)', }, '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.12)', } }}
                                                >
                                                    <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                    {p.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Data Zone Details</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Data Zone Name" /><TextField variant="filled" fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Name" size="medium" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div></Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Environment" />
                                    <TextField select name="environment" value={values.environment} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.environment && Boolean(errors.environment)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>
                                        <MenuItem value="dev" sx={{ fontFamily: "Gilroy-Medium" }}>dev</MenuItem>
                                        <MenuItem value="staging" sx={{ fontFamily: "Gilroy-Medium" }}>staging</MenuItem>
                                        <MenuItem value="prod" sx={{ fontFamily: "Gilroy-Medium" }}>prod</MenuItem>
                                    </TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="environment" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Parent Lake" /><TextField select name="parentSolution" value={values.parentSolution} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.parentSolution && Boolean(errors.parentSolution)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>{availableLakes.map((lake) => (<MenuItem key={lake.id} value={lake.id} sx={{ fontFamily: "Gilroy-Medium" }}>{lake.name}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="parentSolution" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Location" /><TextField select name="location" value={values.location} onChange={(e) => setFieldValue("location", e.target.value)} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.location && Boolean(errors.location)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>{gcpLocationList.map((r) => (<MenuItem key={r} value={r} sx={{ fontFamily: "Gilroy-Medium" }}>{r}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="location" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Type" /><TextField select name="type" value={values.type} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.type && Boolean(errors.type)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>{zoneTypes.map((t) => (<MenuItem key={t} value={t} sx={{ fontFamily: "Gilroy-Medium" }}>{t}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="type" /></div></Grid>
                                <Grid item xs={12}><CompulsoryFieldsLabel fieldName="Description"/><TextField variant="filled" fullWidth multiline rows={3} name="description" value={values.description} onChange={handleChange} onBlur={handleBlur} placeholder="Enter a description for the data zone" size="medium" hiddenLabel error={touched.description && Boolean(errors.description)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="description" /></div></Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3} sx={{ mb: "16px" }}>
                                <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography><TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0", fontFamily: "Gilroy-Medium" } }} /></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Value (Cost Center)" /><TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div></Grid>
                            </Grid>
                            <FieldArray
                                name="costLabelPairs"
                                render={arrayHelpers => (
                                    <div>
                                        {values.costLabelPairs.map((pair, index) => (
                                            <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }}>
                                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Key" /><TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div></Grid>
                                                <Grid item xs={12} sm={5}><CompulsoryFieldsLabel fieldName="Value" /><TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div></Grid>
                                                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 1, sm: '32px' } }}><IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton></Grid>
                                            </Grid>
                                        ))}
                                        <Button variant="text" sx={{ color: "#000000", textTransform: "none", fontFamily: "Gilroy-Medium" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>+ Add Key Value Pair</Button>
                                    </div>
                                )}
                            />
                            <FormControlLabel control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black', },mt:"1" }} />} label="I confirm that the details provided are correct and I wish to proceed." sx={{ mb:"24px", display: 'flex', justifyContent: 'flex-start', fontFamily: "Gilroy-Medium", '& .MuiTypography-root': {fontFamily: "Gilroy-Medium"} }} />
                        </Box>
                        
                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&:hover': { borderColor: 'black', backgroundColor: 'rgba(0,0,0,0.04)' } }} variant="outlined" size="large" onClick={() => navigate("/datasolutions/data-zones")} disabled={isSubmittingForm || isSubmitting}>Cancel</Button>
                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", "&:hover": { backgroundColor: "#333333" }, "&.Mui-disabled": { backgroundColor: "#cccccc", color: "#888888" } }} variant="contained" size="large" type="submit" disabled={isSubmittingForm || isSubmitting || !(isValid && dirty && confirmationChecked)  || isViewer }>{(isSubmittingForm || isSubmitting) ? <CircularProgress size={24} color="inherit" /> : (isEdit ? "Save Changes" : "Add New Data Zone")}</Button>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

// --- Page Component (Wrapper) ---
const AddDataZonesPage: React.FC<AddDataZonesPageProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };
    
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"datasolutions-data-zones"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 3, ml: {xs:2, sm:3, md:5} , mt:2 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "#555555", fontFamily: "Gilroy-Medium" }}>
                        <Link underline="hover" color="inherit" href="/datasolutions/data-zones" sx={{ fontFamily: "Gilroy-Medium" }} onClick={(e)=> {e.preventDefault(); navigate("/datasolutions/data-zones")}}>Data Zones</Link>
                        <Typography color="text.primary" sx={{ fontFamily: "Gilroy-Medium" }}>{isEdit ? "Edit Data Zone" : "Add New Data Zone"}</Typography>
                    </Breadcrumbs>
                    <Box display="flex" alignItems="center" mt={1.5}>
                        <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/data-zones")} sx={{ mr: 0.5 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
                        <Typography fontFamily="Gilroy-Bold" fontSize="20px">{isEdit ? "Edit Data Zone" : "Add New Data Zone"}</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1, alignItems: 'flex-start', pt:0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: { xs: '95%', md: '80%' }, py: 0 }}>
                        <DataZonesFormContent isEdit={isEdit} />
                    </Box>
                </Box>
                <MiniBot />
            </Box>
        </Box>
    );
};

export default AddDataZonesPage;